import { c as _c } from "react/compiler-runtime";
import { AlertTriangle, CheckCircle2, CircleSlash, Copy, ExternalLink } from "lucide-react";
import type { ReactNode } from "react";
import "./money.css";

/** One reference the server returned: an order id, a transaction hash. */
export type ResultRef = {
  /** e.g. "Transaction", "Order ID", "Withdrawal ID". Caller-localised. */
  label: string;
  value: string;
  /** Renders in the mono/LTR-isolated form. Use for hashes and addresses. */
  technical?: boolean;
  href?: string;
};
const ICON = {
  success: CheckCircle2,
  partial: CircleSlash,
  failure: AlertTriangle
} as const;

/**
 * A reference that is only a number (an order, withdrawal or offer id) is not a
 * hash: it reads in the page's own face and digits, on the reading edge —
 * «۱۹۰» in Persian. A copy still takes the value as the server sent it.
 */
const isNumberId = (v: string) => /^\d+$/.test(v);
const pageDigits = (v: string, lang?: "fa" | "en") => lang === "fa" ? v.replace(/\d/g, d => "۰۱۲۳۴۵۶۷۸۹"[Number(d)]) : v;

/**
 * THE RECORD OF WHAT HAPPENED TO THE MONEY, in v2 — a stamped receipt: the outcome
 * (success / partial / failure, a failure announced assertively), the
 * server's own references and nothing invented, a copy and an open control
 * per reference, the partial progress when there is one, and the caller's
 * actions.
 */
export function DurableResult(t0) {
  const $ = _c(29);
  const {
    outcome,
    title,
    description,
    refs,
    progress,
    action,
    secondaryAction,
    labels,
    onCopy,
    lang
  } = t0;
  const Icon = ICON[outcome];
  const t1 = outcome === "failure" ? "alert" : "status";
  const t2 = outcome === "failure" ? "assertive" : "polite";
  let t3;
  if ($[0] !== Icon) {
    t3 = <span className="mn-result-stamp" aria-hidden={true}><Icon /></span>;
    $[0] = Icon;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== title) {
    t4 = <h3>{title}</h3>;
    $[2] = title;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== progress) {
    t5 = progress ? <p className="mn-result-progress">{progress.succeeded}/{progress.total}</p> : null;
    $[4] = progress;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== description) {
    t6 = description ? <p className="mn-result-desc">{description}</p> : null;
    $[6] = description;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== labels?.copy || $[9] !== labels?.open || $[10] !== lang || $[11] !== onCopy || $[12] !== refs) {
    t7 = refs && refs.length > 0 ? <dl className="mn-result-refs">{refs.map(r => <div key={`${r.label}-${r.value}`}><dt>{r.label}</dt><dd>{isNumberId(r.value) ? <span>{pageDigits(r.value, lang)}</span> : <span data-technical={r.technical || undefined}>{r.value}</span>}{onCopy ? <button type="button" className="mn-icon v2-focus" aria-label={`${labels?.copy ?? "Copy"} ${r.label}`} onClick={() => onCopy(r.value)}><Copy aria-hidden={true} /></button> : null}{r.href ? <a className="mn-icon v2-focus" href={r.href} target="_blank" rel="noopener noreferrer" aria-label={`${labels?.open ?? "Open"} ${r.label}`}><ExternalLink aria-hidden={true} /></a> : null}</dd></div>)}</dl> : null;
    $[8] = labels?.copy;
    $[9] = labels?.open;
    $[10] = lang;
    $[11] = onCopy;
    $[12] = refs;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== action || $[15] !== secondaryAction) {
    t8 = action || secondaryAction ? <div className="mn-result-acts">{action}{secondaryAction}</div> : null;
    $[14] = action;
    $[15] = secondaryAction;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t4 || $[18] !== t5 || $[19] !== t6 || $[20] !== t7 || $[21] !== t8) {
    t9 = <div className="mn-result-main">{t4}{t5}{t6}{t7}{t8}</div>;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== outcome || $[24] !== t1 || $[25] !== t2 || $[26] !== t3 || $[27] !== t9) {
    t10 = <section className="mn-result" data-outcome={outcome} role={t1} aria-live={t2}>{t3}{t9}</section>;
    $[23] = outcome;
    $[24] = t1;
    $[25] = t2;
    $[26] = t3;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  return t10;
}